import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const TIERS = [
  {
    name: "Demo",
    price: "Free",
    suffix: "no account",
    description: "Explore the entire app with sample patients. Nothing saves.",
    features: [
      "Full voice + AI extraction",
      "Pre-loaded sample patients",
      "No signup, no card",
      "Chrome & Edge supported",
    ],
    cta: { label: "Try the demo", href: "/demo", testid: "pricing-demo-btn" },
    highlight: false,
  },
  {
    name: "Free Trial",
    price: "15 days",
    suffix: "no credit card",
    description: "Everything in TherapyNote, free for two full weeks.",
    features: [
      "Unlimited patients & plans",
      "Full Evaluation + Daily Notes",
      "Patient history timeline",
      "Clinic-wide reports & exports",
    ],
    cta: { label: "Start free trial", href: "/signup", testid: "pricing-trial-btn" },
    highlight: true,
  },
  {
    name: "Solo",
    price: "Soon",
    suffix: "single therapist",
    description: "For solo practitioners. Subscriptions launching shortly.",
    features: [
      "Everything in Free Trial",
      "Unlimited patients & plans",
      "Audio + transcripts retained",
      "Priority support",
    ],
    cta: { label: "Notify me", href: "/signup", testid: "pricing-paid-btn" },
    highlight: false,
    comingSoon: true,
  },
  {
    name: "Clinic",
    price: "Soon",
    suffix: "min. 5 seats",
    description: "Multi-therapist seats, shared patients, clinic-wide reporting.",
    features: [
      "Everything in Solo, per seat",
      "Centralised patient roster",
      "Per-therapist activity & exports",
      "Clinic admin + billing controls",
    ],
    cta: { label: "Notify me", href: "/signup", testid: "pricing-clinic-btn" },
    highlight: false,
    comingSoon: true,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="bg-[#FAFAFA] py-20 md:py-32"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <span className="font-figtree text-xs uppercase tracking-[0.22em] font-semibold text-[#059669]">
            Pricing
          </span>
          <h2 className="mt-3 font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0F172A] leading-[1.1]">
            Start free. Pay only if it earns its place.
          </h2>
          <p className="mt-4 font-figtree text-base sm:text-lg text-[#475569] leading-relaxed">
            Two weeks of full access, then choose a plan when you&apos;re ready. No
            credit card to begin.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-3xl p-8 flex flex-col border transition-all duration-300 hover:-translate-y-1 ${
                tier.highlight
                  ? "bg-[#064E3B] text-white border-transparent shadow-[0_30px_60px_rgba(6,78,59,0.25)]"
                  : "bg-white text-[#0F172A] border-slate-100 shadow-soft hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#10B981] text-white text-[10px] font-semibold uppercase tracking-[0.18em] shadow-[0_8px_20px_rgba(16,185,129,0.4)]">
                  <Sparkles className="w-3 h-3" />
                  Start here
                </div>
              )}

              <div
                className={`font-figtree text-xs uppercase tracking-[0.18em] font-semibold ${
                  tier.highlight ? "text-[#86EFAC]" : "text-[#059669]"
                }`}
              >
                {tier.name}
              </div>

              <div className="mt-3 flex items-baseline gap-2">
                <span
                  className={`font-outfit text-4xl font-bold tracking-tight ${
                    tier.highlight ? "text-white" : "text-[#0F172A]"
                  }`}
                >
                  {tier.price}
                </span>
                <span
                  className={`font-figtree text-sm ${
                    tier.highlight ? "text-[#D1FAE5]/80" : "text-[#94A3B8]"
                  }`}
                >
                  · {tier.suffix}
                </span>
              </div>

              <p
                className={`mt-3 font-figtree text-sm leading-relaxed ${
                  tier.highlight ? "text-[#D1FAE5]/90" : "text-[#475569]"
                }`}
              >
                {tier.description}
              </p>

              <ul className="mt-6 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-3 font-figtree text-sm ${
                      tier.highlight ? "text-white/90" : "text-[#0F172A]"
                    }`}
                  >
                    <Check
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        tier.highlight ? "text-[#86EFAC]" : "text-[#10B981]"
                      }`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              {tier.comingSoon ? (
                <div className="mt-8 block">
                  <Button
                    data-testid={tier.cta.testid}
                    disabled
                    className="w-full rounded-full py-6 font-figtree text-sm font-medium bg-slate-100 text-slate-400 cursor-not-allowed"
                  >
                    Coming soon
                  </Button>
                </div>
              ) : (
                <Link to={tier.cta.href} className="mt-8 block">
                  <Button
                    data-testid={tier.cta.testid}
                    className={`w-full rounded-full py-6 font-figtree text-sm font-medium transition-all duration-300 ${
                      tier.highlight
                        ? "bg-[#10B981] hover:bg-[#34D399] text-white"
                        : "bg-[#0F172A] hover:bg-[#1E293B] text-white"
                    }`}
                  >
                    {tier.cta.label}
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
